/**
 * GeoServicePage — Renders a single Geo SEO landing page.
 * Route: /locations/:stateSlug/:citySlug/:serviceId
 * 
 * Fetches content from the API, renders with proper SEO meta tags via
 * react-helmet-async. The edge renderer captures the fully hydrated DOM
 * (including all meta tags) for crawler traffic.
 */
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import useGeoSEO from '../hooks/useGeoSEO';

const API = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = 'https://localbrandingpro.com';
const BRAND = 'Local Branding Pro';
const PHONE = '(817) 918-0589';
const PHONE_TEL = '+18179180589';

const SERVICE_LABELS = {
  'local-seo': 'Local SEO',
  'social-media': 'Social Media Marketing',
  'reputation-management': 'Reputation Management',
  'google-ads': 'Google Ads & PPC',
  'web-design': 'Web Design',
  'content-marketing': 'Content Marketing',
  'google-business-profile': 'Google Business Profile',
};

export default function GeoServicePage() {
  const { stateSlug, citySlug, serviceId } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`${API}/api/seo/geo-page/${stateSlug}/${citySlug}/${serviceId}`)
      .then(r => { if (!r.ok) throw new Error('Page not found'); return r.json(); })
      .then(data => { setPage(data); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [stateSlug, citySlug, serviceId]);

  // Direct DOM meta injection — MUST be called before any early returns (hooks rules)
  useGeoSEO(page);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="animate-pulse text-slate-400">Loading...</div>
    </div>
  );

  if (error || !page) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-slate-300 mb-4">404</h1>
        <p className="text-slate-600 mb-4">This page hasn't been generated yet.</p>
        <Link to="/locations" className="text-blue-600 hover:underline">Browse all locations</Link>
      </div>
    </div>
  );

  const content = page.content || {};
  const city = page.city_name || '';
  const stateName = page.state_name || '';
  const stateAbbr = page.state_abbr || '';
  const serviceName = page.service_name || SERVICE_LABELS[serviceId] || serviceId;
  const population = page.population || 0;
  const county = page.county || '';

  const title = content.meta_title || `${serviceName} in ${city}, ${stateAbbr} | ${BRAND}`;
  const desc = content.meta_description || `Professional ${serviceName} services for businesses in ${city}, ${stateAbbr}.`;

  const faqs = content.faq || [];

  // Other services for crosslinks
  const otherServices = Object.entries(SERVICE_LABELS).filter(([id]) => id !== serviceId);

  return (
    <>
      {/* Hero */}
      <header style={{ background: 'linear-gradient(135deg, #0f172a, #1e3a8a)', color: '#fff', padding: '48px 24px' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <nav style={{ fontSize: 13, color: '#93c5fd', marginBottom: 12 }} data-testid="geo-breadcrumbs">
            <Link to="/" style={{ color: '#93c5fd', textDecoration: 'none' }}>Home</Link> &rsaquo;{' '}
            <Link to="/locations" style={{ color: '#93c5fd', textDecoration: 'none' }}>Locations</Link> &rsaquo;{' '}
            <Link to={`/locations/${stateSlug}`} style={{ color: '#93c5fd', textDecoration: 'none' }}>{stateName || stateAbbr}</Link> &rsaquo;{' '}
            <span>{city}</span>
          </nav>
          <h1 style={{ fontSize: 'clamp(28px, 5vw, 44px)', fontWeight: 800, margin: '0 0 12px' }} data-testid="geo-page-h1">
            {content.h1 || `${serviceName} in ${city}, ${stateAbbr}`}
          </h1>
          {content.intro && <div style={{ fontSize: 17, color: '#cbd5e1', maxWidth: 760 }} dangerouslySetInnerHTML={{ __html: content.intro }} />}
          <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 10 }}>
            {city}, {stateAbbr} &bull; Population: {population.toLocaleString()} &bull; {county} County
          </div>
          <div style={{ marginTop: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <a href={`${SITE_URL}/#pricing`} style={{ display: 'inline-block', background: '#fff', color: '#1e3a8a', fontWeight: 700, padding: '12px 28px', borderRadius: 8, textDecoration: 'none' }}>Get Started</a>
            <a href={`tel:${PHONE_TEL}`} style={{ display: 'inline-block', background: 'transparent', color: '#fff', fontWeight: 700, padding: '12px 28px', borderRadius: 8, textDecoration: 'none', border: '2px solid rgba(255,255,255,0.3)' }}>Call {PHONE}</a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main style={{ maxWidth: 900, margin: '0 auto', padding: '40px 24px' }}>
        <article>
          {content.body && <div dangerouslySetInnerHTML={{ __html: content.body }} style={{ lineHeight: 1.7 }} />}

          {content.landmarks_section && (
            <aside style={{ background: 'linear-gradient(135deg, #faf5ff, #f0f9ff)', border: '1px solid #e0e7ff', padding: 24, borderRadius: 12, margin: '24px 0' }}>
              <div dangerouslySetInnerHTML={{ __html: content.landmarks_section }} />
            </aside>
          )}

          {content.local_section && (
            <aside style={{ background: '#f1f5f9', borderLeft: '4px solid #1e3a8a', padding: 24, borderRadius: '0 8px 8px 0', margin: '24px 0' }}>
              <h2 style={{ marginTop: 0 }}>Why {city} Businesses Choose {BRAND}</h2>
              <div dangerouslySetInnerHTML={{ __html: content.local_section }} />
            </aside>
          )}

          {/* Trust bar */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, justifyContent: 'center', padding: 24, background: '#f8fafc', borderRadius: 12, margin: '24px 0', fontSize: 14, color: '#475569' }}>
            <div>&#10003; Full-Service Digital Marketing</div>
            <div>&#10003; Bilingual EN/ES Support</div>
            <div>&#10003; Serving {city} &amp; Nationwide</div>
          </div>

          {/* CTA Box */}
          {content.cta && (
            <div style={{ background: 'linear-gradient(135deg, #1e3a8a, #3730a3)', color: '#fff', padding: 32, borderRadius: 12, margin: '32px 0', textAlign: 'center' }}>
              <h2 style={{ margin: '0 0 12px' }}>Ready to Grow Your {city} Business?</h2>
              <div dangerouslySetInnerHTML={{ __html: content.cta }} />
              <div style={{ marginTop: 16 }}>
                <a href={`${SITE_URL}/#pricing`} style={{ display: 'inline-block', background: '#fff', color: '#1e3a8a', fontWeight: 700, padding: '12px 28px', borderRadius: 8, textDecoration: 'none', marginRight: 12 }}>Get Started in {city}</a>
                <a href={`tel:${PHONE_TEL}`} style={{ display: 'inline-block', color: '#fff', fontWeight: 700, padding: '12px 28px', borderRadius: 8, textDecoration: 'none', border: '2px solid rgba(255,255,255,0.3)' }}>Call {PHONE}</a>
              </div>
            </div>
          )}
        </article>

        {/* FAQ Section */}
        {faqs.length > 0 && (
          <section style={{ background: '#f8fafc', padding: '32px 24px', marginTop: 40, borderRadius: 12 }} data-testid="geo-faq-section">
            <h2 style={{ marginTop: 0 }}>Frequently Asked Questions</h2>
            {faqs.map((faq, i) => (
              <details key={i} style={{ background: '#fff', padding: '14px 18px', marginBottom: 10, border: '1px solid #e2e8f0', borderRadius: 8 }}>
                <summary style={{ fontWeight: 600, cursor: 'pointer', color: '#0f172a' }}>{faq.q}</summary>
                <p style={{ marginTop: 8, color: '#475569' }}>{faq.a}</p>
              </details>
            ))}
          </section>
        )}

        {/* Related services crosslinks */}
        <nav style={{ borderTop: '1px solid #e2e8f0', paddingTop: 24, marginTop: 32, fontSize: 14, color: '#64748b' }}>
          <strong>Other {BRAND} services in {city}:</strong>{' '}
          {otherServices.map(([sid, label], i) => (
            <span key={sid}>
              {i > 0 && ' · '}
              <Link to={`/locations/${stateSlug}/${citySlug}/${sid}`} style={{ color: '#1e3a8a', textDecoration: 'none' }}>{label}</Link>
            </span>
          ))}
        </nav>
      </main>

      {/* Footer */}
      <footer style={{ background: '#0f172a', color: '#cbd5e1', padding: '32px 24px', textAlign: 'center', fontSize: 14 }}>
        <p><strong>{BRAND}</strong> — Professional {serviceName} for {city}, {stateAbbr} businesses</p>
        <p><a href={`tel:${PHONE_TEL}`} style={{ color: '#93c5fd' }}>{PHONE}</a> &bull; <a href="mailto:info@localbrandingpro.com" style={{ color: '#93c5fd' }}>info@localbrandingpro.com</a> &bull; <a href={SITE_URL} style={{ color: '#93c5fd' }}>localbrandingpro.com</a></p>
      </footer>
    </>
  );
}
